import dayjs from 'dayjs';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

import type {ChannelApiModel} from '../../../api/channel';
import type {
  ConferenceApiModel,
  ConferenceExpandedCommon,
} from '../../../api/conference';
import type {CourseApiModel} from '../../../api/course';
import type {ApiListResponseExpanded, Expand} from '../../../api/types';
import type {BNClientChannelsReadParams} from '../../../api/types/bnrks/methods';
import type {
  BatchMethodStatus,
  MethodStatus,
} from '../../../api/utils/entityStore';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../utils/store';
import type {Nullable} from '../../../utils/utility-types';
import type {Props as ChatBannerProps} from '../components/ChatConferenceBanner/types';

import {conferenceExpandCommon, conferenceStore} from '../../../api/conference';
import {eventStore} from '../../../api/event';
import {apiRequest} from '../../../api/utils/apiRequest';
import {expandObj} from '../../../api/utils/expand';
import {alert} from '../../../components/alert';
import {conferenceJoin} from '../../../services/entity-action/conference/conference.join';
import {conferenceStart} from '../../../services/entity-action/conference/conference.start';
import {conferenceStop} from '../../../services/entity-action/conference/conference.stop';
import {getCourseNextUpcomingConference} from '../../../services/entity-action/course/course.get-next-upcoming-conference';
import {isCancelCommand} from '../../../services/entity-action/utils';
import {pickCoreOrB2b} from '../../../utils/coreOrB2b/pickCoreOrB2b';
import {datetimeObjToISOString} from '../../../utils/datetime';
import {
  isConferenceStarted,
  isMeConferenceHost,
} from '../../../utils/entity/conference';
import {ALLOWED_CONFERENCE_START_BEFORE_MINS} from '../../../utils/entity/conference/getConferenceAbleStartDate';
import {isChatConference} from '../../../utils/entity/conference/isChatConference';
import {MINS_FOR_CONFERENCE_EXPIRATION} from '../../../utils/entity/conference/isConferenceExpired';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {AppError} from '../../../utils/errors/app';
import {emitter, WS_RECEIVE_MESSAGE_EVENT} from '../../../utils/eventEmitter';
import {logger} from '../../../utils/logger';
import {AsyncBusyManager, ReactionDisposeManager} from '../../../utils/store';
import {isDefined} from '../../../utils/typecheck';
import {formatSecondsToMMSS} from '../utils/timer';
import {conferenceStopHandler} from './ConferenceStopHandler.store';

const channelExpand: Expand = {
  channel: ['resource_id'],
};

const FETCH_UPCOMING_CONFERENCE_INTERVAL = 5 * 60 * 1000; // 5 minutes

const WARN_ABOUT_CONFERENCE_END_BEFORE_MINS = 5 * 60; // 5 minutes

type ReasonKey = 'start' | 'stop';

export class ChatConferenceStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  readonly busy = new AsyncBusyManager<ReasonKey>();
  conference: ConferenceExpandedCommon | null = null;
  disposer = new ReactionDisposeManager();
  private channelId = '';
  private conferenceExpirationTimeout = 0;
  private conferenceTimer = 0;
  private course: Nullable<CourseApiModel> = null;
  private currentTimestampMs = Date.now();
  private duration = 0;
  private fetchConferenceTimeout = 0;
  private isConferenceExpired = false;
  private isConferenceStarted = false;
  private isConferenceStartedWsMessageHandled = false;
  private secondsBeforeConferenceStart = 0;

  constructor() {
    this.busy.observe(this, {
      start: 'start',
      stop: 'stop',
    });

    this.onWsMessage = this.onWsMessage.bind(this);

    makeAutoObservable(this, {}, {autoBind: true});
  }

  init(channelId: string) {
    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);

    this.setupReactions();

    this.channelId = channelId;

    this.fetchCourse();
  }

  destruct() {
    this.course = null;
    this.conference = null;

    this.disposer.dispose();

    clearInterval(this.conferenceTimer);
    clearTimeout(this.fetchConferenceTimeout);
    clearTimeout(this.conferenceExpirationTimeout);

    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);

    this.isConferenceStartedWsMessageHandled = false;
  }

  get bannerType(): ChatBannerProps['type'] | undefined {
    if (!this.conference) {
      return;
    }

    if (this.isStartAvailable) {
      return 'start';
    }

    // for client we only show start banner
    if (!this.isMeCoachForConference) {
      return;
    }

    if (this.isConferenceShouldBeStopped) {
      return 'overdue';
    }

    if (this.isConferenceStarted) {
      return 'inProgress';
    }
  }

  get conferenceStartTime() {
    if (!this.conference) {
      return '';
    }

    return dayjs(datetimeObjToISOString(this.conference.start_date)).format(
      'LT',
    );
  }

  get isConferenceShouldBeStopped() {
    return this.isConferenceStarted && this.secondsBeforeConferenceEnd <= 0;
  }

  get isMeCoachForConference() {
    return isMeCoachForEntity(this.conference);
  }

  get isStartAvailable() {
    return (
      !this.isConferenceStarted &&
      !this.isConferenceExpired &&
      this.secondsBeforeConferenceStart <=
        ALLOWED_CONFERENCE_START_BEFORE_MINS * 60
    );
  }

  get isStopAvailable() {
    return this.isConferenceStarted;
  }

  get secondsAfterConferenceStart() {
    if (!this.isConferenceStarted) {
      return 0;
    }

    // after the conference has started, secondsBeforeConferenceStart is negative
    return Math.abs(this.secondsBeforeConferenceStart);
  }

  get secondsBeforeConferenceEnd() {
    if (!this.isConferenceStarted || !this.conference) {
      return 0;
    }

    const currentTimestampMs = this.currentTimestampMs;
    const plannedStartDate = dayjs(
      datetimeObjToISOString(this.conference.start_date),
    );
    const plannedDurationSeconds = this.conference.duration || this.duration;

    return plannedStartDate
      .add(plannedDurationSeconds, 'second')
      .diff(dayjs(currentTimestampMs), 'seconds');
  }

  get shouldShowPlainInProgressBanner() {
    return (
      this.bannerType === 'inProgress' && !this.shouldWarnAboutConferenceEnd
    );
  }

  get shouldWarnAboutConferenceEnd() {
    return this.isConferenceStarted
      ? this.secondsBeforeConferenceEnd <= WARN_ABOUT_CONFERENCE_END_BEFORE_MINS
      : false;
  }

  get timeBeforeEndString() {
    const secondsBeforeEnd = this.secondsBeforeConferenceEnd;

    if (secondsBeforeEnd <= 0) {
      return '';
    }

    return formatSecondsToMMSS(secondsBeforeEnd);
  }

  async start() {
    try {
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      logger.event('chat_session_start_tap', {
        place: 'chat',
      });

      const result = await conferenceStart.asCoach({
        entity: this.conference,
        params: {
          expand: conferenceExpandCommon,
        },
      });

      if (result) {
        runInAction(() => {
          this.isConferenceStarted = true;

          this.conference = result.conference as ConferenceExpandedCommon;
        });
      }

      conferenceStopHandler.setJoinedConference(this.conference);
    } catch (error) {
      logger.error(error);
      alert.error(error);
    }
  }

  startOrStopConference() {
    if (!this.isMeCoachForConference) {
      return;
    }

    if (this.isStartAvailable) {
      this.start();
    } else if (this.isStopAvailable) {
      this.stop();
    }
  }

  async stop() {
    try {
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      if (!isMeConferenceHost(this.conference)) {
        return;
      }

      logger.event('chat_session_end_tap', {
        place: 'chat',
      });

      const result = await conferenceStop({
        entity: this.conference,
        params: {
          conference_type: this.conference.type,
        },
      });

      if (isCancelCommand(result)) {
        return;
      }

      conferenceStore.coach.localUpdate(this.conference!, {
        session_id: null,
        status: 'past',
      });

      runInAction(() => {
        this.isConferenceExpired = false;
        this.isConferenceStarted = false;

        this.conference = null;
      });
    } catch (error) {
      logger.error(error);
      alert.error(error);
    }
  }

  private calcDuration() {
    if (!this.conference) {
      return;
    }

    const startDate = dayjs(datetimeObjToISOString(this.conference.start_date));
    const endDate = dayjs(datetimeObjToISOString(this.conference.end_date));

    this.duration = endDate.diff(startDate, 'seconds');
  }

  private calcSecondsBeforeConferenceStart() {
    if (!this.conference) {
      return 0;
    }

    const startDate = dayjs(
      datetimeObjToISOString(
        this.conference.started_time || this.conference.start_date,
      ),
    );
    const now = dayjs(this.currentTimestampMs);

    this.secondsBeforeConferenceStart = startDate.diff(now, 'seconds');
  }

  private checkIsConferenceStarted() {
    this.isConferenceStarted = isConferenceStarted(this.conference);
  }

  private async fetchCourse() {
    try {
      const {
        _expanded,
        channel,
      }: {
        _expanded: ApiListResponseExpanded<ChannelApiModel>['_expanded'];
        channel: ChannelApiModel;
      } = await apiRequest<BNClientChannelsReadParams>({
        method: 'client.channels.read',
        params: {
          _id: this.channelId,
          expand: channelExpand,
        },
      });

      const expandedChannel = expandObj<
        ChannelApiModel,
        {
          resource: CourseApiModel;
        }
      >(channel, {
        expand: channelExpand,
        expanded: _expanded,
      });

      runInAction(() => {
        this.course = expandedChannel.resource;
      });
    } catch (error) {
      logger.error(error);
    }
  }

  private async fetchNextUpcomingConference() {
    try {
      if (!this.course) {
        return;
      }

      clearTimeout(this.fetchConferenceTimeout);

      const nextConference = await getCourseNextUpcomingConference({
        entity: this.course,
      });

      if (isChatConference(nextConference)) {
        runInAction(() => {
          this.isConferenceStarted = isConferenceStarted(nextConference);

          this.conference = nextConference;

          this.initConference();
        });
      }

      this.fetchConferenceTimeout = +setTimeout(() => {
        this.fetchNextUpcomingConference();
      }, FETCH_UPCOMING_CONFERENCE_INTERVAL);
    } catch (error) {
      logger.error(error);
    }
  }

  private initConference() {
    if (!this.conference) {
      return;
    }

    eventStore.markReadAll({
      limit: 500,
      query: [['context_ids', 'in', [this.conference._id]]],
    });

    if (this.isConferenceStarted) {
      this.join();
    }
  }

  private async join() {
    try {
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      const result = await conferenceJoin({
        entity: this.conference,
        params: {
          expand: conferenceExpandCommon,
        },
      });

      if (result) {
        runInAction(() => {
          this.conference = result.conference as ConferenceExpandedCommon;
        });
      }

      conferenceStopHandler.setJoinedConference(this.conference);
    } catch (error) {
      logger.error(error);
    }
  }

  private onWsMessage(message: any) {
    const isThisConference = message.conference?._id === this.conference?._id;

    const isStartedEvent = message.event.type === 'conference_started';

    if (isStartedEvent && !this.isConferenceStartedWsMessageHandled) {
      // B2B backend sends conference_started event twice
      this.isConferenceStartedWsMessageHandled = true;

      if (isThisConference) {
        this.isConferenceStarted = true;

        this.initConference();

        clearTimeout(this.conferenceExpirationTimeout);
      } else {
        // if we receive event for another conference,
        // that meanse that another conference took the place and we need to init it here
        this.fetchNextUpcomingConference();
      }
    }

    if (
      ['conference_stopped', 'conference_stopped_automatically'].includes(
        message.event.type,
      ) &&
      isThisConference
    ) {
      this.conference = null;
    }
  }

  private resetState() {
    this.course = null;
    this.conference = null;
    this.isConferenceStarted = false;
    this.isConferenceExpired = false;
    this.secondsBeforeConferenceStart = 0;
    this.currentTimestampMs = Date.now();
    this.duration = 0;
    this.isConferenceStartedWsMessageHandled = false;

    this.disposer.dispose();

    clearInterval(this.conferenceTimer);
    clearTimeout(this.fetchConferenceTimeout);
    clearTimeout(this.conferenceExpirationTimeout);
  }

  private setupReactions() {
    this.disposer.watch(
      'course',
      reaction(
        () => isDefined(this.course),
        () => {
          this.fetchNextUpcomingConference();
        },
      ),
    );

    this.disposer.watch(
      'conference',
      reaction(
        () => this.conference,
        conference => {
          if (isDefined(conference)) {
            this.calcSecondsBeforeConferenceStart();
            this.checkIsConferenceStarted();
            this.calcDuration();
            this.startTimer();
          } else {
            this.stopTimer();
            this.fetchNextUpcomingConference();

            runInAction(() => {
              this.isConferenceStarted = false;
              this.isConferenceExpired = false;
            });
          }

          this.isConferenceStartedWsMessageHandled = false;
        },
      ),
    );

    this.disposer.watch(
      'channelId',
      reaction(
        () => this.channelId,
        (prev, next) => {
          if (prev !== next) {
            this.resetState();
          }
        },
      ),
    );

    this.disposer.watchMany<MethodStatus<ConferenceApiModel>>({
      effect: arg => {
        if (arg.success && isChatConference(arg.entity)) {
          this.fetchNextUpcomingConference();
        }
      },
      reactionConfigs: [
        {
          expression: () => conferenceStore.coach.creating,
          key: 'create',
        },
        {
          expression: () => conferenceStore.coach.updating,
          key: 'update',
        },
      ],
    });

    this.disposer.watch(
      'delete',
      reaction(
        () => conferenceStore.coach.deleting,
        deleting => {
          if (
            deleting.success &&
            this.conference?._id === deleting.entity?._id
          ) {
            this.conference = null;
          }
        },
      ),
    );

    this.disposer.watchMany<BatchMethodStatus<ConferenceApiModel>>({
      effect: arg => {
        if (arg.success && arg.entities.some(isChatConference)) {
          this.fetchNextUpcomingConference();
        }
      },
      reactionConfigs: [
        {
          expression: () => conferenceStore.coach.creatingBatch,
          key: 'createBatch',
        },
        {
          expression: () => conferenceStore.coach.updatingBatch,
          key: 'updateBatch',
        },
      ],
    });

    this.disposer.watch(
      'deleteBatch',
      reaction(
        () => conferenceStore.coach.deletingBatch,
        deleting => {
          if (
            deleting.success &&
            deleting.entities.some(c => c._id === this.conference?._id)
          ) {
            this.conference = null;
          }
        },
      ),
    );
  }

  private startExpirationTimer() {
    clearTimeout(this.conferenceExpirationTimeout);

    if (this.conference && !this.isConferenceStarted) {
      const expirationDate = dayjs(
        datetimeObjToISOString(this.conference.start_date),
      ).add(pickCoreOrB2b(MINS_FOR_CONFERENCE_EXPIRATION)!, 'minutes');
      const now = dayjs();

      this.conferenceExpirationTimeout = +setTimeout(
        () => {
          runInAction(() => {
            this.conference = null;
          });
        },
        expirationDate.diff(now, 'millisecond'),
      );
    }
  }

  private startTimer() {
    clearInterval(this.conferenceTimer);

    runInAction(() => {
      this.currentTimestampMs = Date.now();
      this.calcSecondsBeforeConferenceStart();
    });

    this.conferenceTimer = +setInterval(() => {
      runInAction(() => {
        this.currentTimestampMs = Date.now();
        this.calcSecondsBeforeConferenceStart();
      });
    }, 1000);

    this.startExpirationTimer();
  }

  private stopTimer() {
    this.secondsBeforeConferenceStart = 0;
    this.currentTimestampMs = Date.now();
    clearInterval(this.conferenceTimer);
    clearTimeout(this.conferenceExpirationTimeout);
  }
}
